import { AppstoreOutlined, BarsOutlined, FolderAddOutlined, UploadOutlined } from "@ant-design/icons"
import { Breadcrumb, Button, Card, Empty, Flex, Radio, Skeleton, Spin, Tooltip, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { SearchBar } from "../../../components/Input/SearchBar.tsx"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { ActivityDrawer } from "../../Files/components/ActivityDrawer.tsx"
import { AssignDocumentTypeModal } from "../../Files/components/AssignDocumentTypeModal.tsx"
import { BulkActionBar } from "../../Files/components/BulkActionBar.tsx"
import { CommentsDrawer } from "../../Files/components/CommentsDrawer.tsx"
import { CreateFolderCard } from "../../Files/components/CreateFolderCard.tsx"
import { CreateFolderRow } from "../../Files/components/CreateFolderRow.tsx"
import { FileCard } from "../../Files/components/FileCard.tsx"
import { FileViewer } from "../../Files/components/FileViewer.tsx"
import { FilesGridSelectAll } from "../../Files/components/FilesGridSelectAll.tsx"
import { FilesListView } from "../../Files/components/FilesListView.tsx"
import { FilesSortSelect } from "../../Files/components/FilesSortSelect.tsx"
import { FolderCard } from "../../Files/components/FolderCard.tsx"
import { MoveFileModal } from "../../Files/components/MoveFileModal.tsx"
import { MoveFolderModal } from "../../Files/components/MoveFolderModal.tsx"
import { NewFolderModal } from "../../Files/components/NewFolderModal.tsx"
import { RejectReviewModal } from "../../Files/components/RejectReviewModal.tsx"
import { RequestReviewModal } from "../../Files/components/RequestReviewModal.tsx"
import { ReviewDrawer } from "../../Files/components/ReviewDrawer.tsx"
import { ShareDialog } from "../../Files/components/ShareDialog.tsx"
import { UploadNewVersionModal } from "../../Files/components/UploadNewVersionModal.tsx"
import { VersionHistoryDrawer } from "../../Files/components/VersionHistoryDrawer.tsx"
import { useFilesView } from "../../Files/helpers/files-view-preference.ts"
import { FOLDER_LOADER_DELAY_MS } from "../../Files/helpers/folder-loader-delay.ts"
import { sortFileItems, sortFolders } from "../../Files/helpers/sort-file-items.ts"
import { useFavorites } from "../../Files/hooks/useFavorites.ts"
import { useFileActions } from "../../Files/hooks/useFileActions.ts"
import { useFilesData } from "../../Files/hooks/useFilesData.ts"
import { type FileItem } from "../../Files/types/file-item.types.ts"
import { DEFAULT_SORT_ORDER, type FilesSortOrder } from "../../Files/types/files-filters.types.ts"
import { type FolderListItem } from "../../Files/types/folder.types.ts"
import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"
import { RenameFileModal } from "../../Project/ProjectViewPage/components/RenameFileModal.tsx"
import { UploadDocumentModal } from "../../Project/ProjectViewPage/components/sections/ProjectViewDocuments/UploadDocumentModal.tsx"

const { Title, Paragraph, Text } = Typography

// A stable, non-empty key: `useFavorites`/`useFilesView` persist per key, and
// there is exactly one company workspace per team, unlike a project's many.
const COMPANY_SCOPE_KEY = "company"

/** Company Files (Phase C1) — the company's own folder workspace, a peer to
 * a project's own Files tab, scoped to `team` instead of one project. Reuses
 * the same hooks (`useFilesData`/`useFileActions`, both widened to accept an
 * absent `projectId` as "company scope") and the same leaf components
 * (FileCard/FolderCard/FilesListView/BulkActionBar/the Move/New Folder/File
 * Viewer modals) a project's Files tab uses — deliberately a single, simpler
 * view (no Recent/Favorites/Albums/Trash nav: Company Albums is a later
 * phase, and Company Trash reuses the existing Global Trash tab instead)
 * rather than a second fork of `ProjectViewFilesSection.tsx`.
 *
 * "Link Record" and "Share with customer" are not wired into the File
 * Viewer here — both are proposal/contract/customer concepts a company
 * document has no lead to resolve against.
 *
 * A cross-workspace folder move (company file -> project folder, or the
 * reverse) is deliberately unsupported — `MoveFileModal`/`MoveFolderModal`
 * only ever list this team's OWN company folders, and the server refuses
 * the rest (`folder_belongs_to_project`).
 */
export function GlobalFilesCompanyFilesTab() {
  const { t } = useTranslation()
  const currentUserId = useAppSelector((state) => state.auth?.userInfo?.id)
  const moduleDocs = useAppSelector(
    (state) => state.auth?.permissions?.codes?.projects?.docs
  )
  const [view, setView] = useFilesView()
  const [previewContext, setPreviewContext] = useState<FileItem[]>([])
  const [sortOrder, setSortOrder] = useState<FilesSortOrder>(DEFAULT_SORT_ORDER)

  const createAllowed = !!moduleDocs?.project_doc_create
  const rights = useMemo(() => {
    const editAllowed = !!moduleDocs?.project_doc_edit
    const deleteAllowed = !!moduleDocs?.project_doc_delete
    const manageOthers = !!moduleDocs?.project_doc_manage_others
    return {
      currentUserId,
      createAllowed,
      editAllowed,
      editForeignAllowed: editAllowed && manageOthers,
      deleteAllowed,
      deleteForeignAllowed: deleteAllowed && manageOthers,
      manageOthersAllowed: manageOthers
    }
  }, [moduleDocs, currentUserId, createAllowed])

  const data = useFilesData({ initialNav: "folders", rights, sort: sortOrder })
  const actions = useFileActions({
    currentFolderId: data.currentFolderId,
    fileItems: data.fileItems,
    rights,
    reload: data.reload
  })
  const favorites = useFavorites(COMPANY_SCOPE_KEY)

  const { dateFormat, timezone } = useDateFormatter()
  const sortedFileItems = useMemo(
    () => sortFileItems(data.fileItems, sortOrder, { dateFormat, timezone }),
    [data.fileItems, sortOrder, dateFormat, timezone]
  )
  const sortedFolders = useMemo(
    () => sortFolders(data.folders, sortOrder, { dateFormat, timezone }),
    [data.folders, sortOrder, dateFormat, timezone]
  )

  const previewFrom = (items: FileItem[]) => (file: FileItem) => {
    setPreviewContext(items)
    actions.setPreviewFile(file)
  }

  const renderFileTable = (items: FileItem[], folders: FolderListItem[] = []) => (
    <FilesListView
      items={items}
      folders={folders}
      folderActions={actions.folderActions}
      onOpenFolder={data.openFolder}
      actions={actions.fileActions}
      selectedKeys={actions.selectedKeys}
      onSelectionChange={actions.setSelection}
      onPreview={previewFrom(items)}
      isFavorite={(file) => favorites.isFavorite(file.key)}
      onToggleFavorite={(file) => favorites.toggleFavorite(file.key)}
    />
  )

  const renderFileItems = (items: FileItem[]) => {
    const onPreview = previewFrom(items)
    return view === "list" ? (
      renderFileTable(items)
    ) : (
      <Flex vertical gap="small">
        <FilesGridSelectAll
          fileKeys={items.map((file) => file.key)}
          selectedKeys={actions.selectedKeys}
          onSelectionChange={actions.setSelection}
        />
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(160px,1fr))]">
          {items.map((file) => (
            <FileCard
              key={file.key}
              file={file}
              onPreview={onPreview}
              actions={actions.fileActions(file)}
              selected={actions.selectedKeys.has(file.key)}
              onToggleSelect={actions.toggleSelection}
              draggable={file.canMove}
              favorited={favorites.isFavorite(file.key)}
              onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
            />
          ))}
        </div>
      </Flex>
    )
  }

  const renderFolderGrid = (items: FolderListItem[]) => (
    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
      {createAllowed && <CreateFolderCard onClick={() => actions.setNewFolderOpen(true)} />}
      {items.map((folder) => {
        const canManage = actions.canManageFolder(folder)
        return (
          <FolderCard
            key={folder.id}
            folder={folder}
            onOpen={data.openFolder}
            actions={actions.folderActions(folder)}
            draggable={canManage}
            onDropFile={(fileKey) => actions.moveFileById(fileKey, folder.id)}
            onDropFolder={
              canManage ? (folderId) => void actions.moveFolderTo(folderId, folder.id) : undefined
            }
          />
        )
      })}
    </div>
  )

  const handlePreviewVersion = (document: ProjectDocument) => {
    // The workspace-agnostic file mapper lives in useFilesData's own module;
    // simplest here is to just find the freshly-reloaded row by id.
    const item = data.fileItems.find((f) => f.id === document.id)
    if (item) {
      setPreviewContext([item])
      actions.setPreviewFile(item)
    }
  }

  const isEmpty =
    data.hasLoaded && !data.isSearching && !sortedFolders.length && !sortedFileItems.length

  return (
    <Flex vertical gap="middle">
      <Flex align="center" justify="space-between" wrap="wrap" gap="small">
        <Flex vertical gap={0}>
          <Title level={5} className="!mb-0">
            {t("globalFiles.company.title", "Company Files")}
          </Title>
          <Paragraph type="secondary" className="!mb-0">
            {t(
              "globalFiles.company.subtitle",
              "Documents and folders that belong to the company, not a specific project or lead."
            )}
          </Paragraph>
        </Flex>
        <Flex gap="small">
          {createAllowed && (
            <Button
              icon={<UploadOutlined />}
              type="primary"
              onClick={() => actions.setUploadDocumentOpen(true)}
            >
              {t("files.actionUpload", "Upload")}
            </Button>
          )}
          {createAllowed && (
            <Button icon={<FolderAddOutlined />} onClick={() => actions.setNewFolderOpen(true)}>
              {t("files.actionNewFolder")}
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex gap="small" align="center" wrap="wrap">
        <SearchBar
          allowClear
          placeholder={t("files.searchPlaceholder")}
          onSearch={(value) => data.setSearch(value ?? "")}
          className="max-w-md"
        />
      </Flex>

      {actions.selectedFiles.length > 0 && (
        <BulkActionBar
          count={actions.selectedFiles.length}
          onMove={actions.bulkCanMove ? () => actions.setMovingFiles(actions.selectedFiles) : undefined}
          onDownload={actions.bulkDownload}
          onDelete={() => void actions.bulkDelete()}
          onClear={actions.clearSelection}
          canDelete={actions.bulkCanDelete}
          onFavorite={() => favorites.setFavorites(actions.selectedFiles.map((f) => f.key), true)}
          onUnfavorite={() => favorites.setFavorites(actions.selectedFiles.map((f) => f.key), false)}
          allFavorited={actions.selectedFiles.every((f) => favorites.isFavorite(f.key))}
          statusOptions={actions.bulkCanEditClassification ? actions.bulkAvailableStatusOptions : undefined}
          statusApplicable={actions.bulkCanEditClassification}
          statusesFailed={actions.statusesFailed}
          onChangeStatus={(value) => void actions.bulkChangeStatus(actions.selectedFiles, value)}
          documentTypeOptions={actions.bulkCanEditClassification ? actions.documentTypeOptions : undefined}
          onChangeDocumentType={(value) => void actions.bulkChangeDocumentType(actions.selectedFiles, value)}
        />
      )}

      {data.hasListing && (
        <Flex justify="end" align="center" gap="small">
          <FilesSortSelect value={sortOrder} onChange={setSortOrder} />
          <Radio.Group
            value={view}
            onChange={(e) => setView(e.target.value)}
            optionType="button"
            options={[
              {
                label: (
                  <span aria-label={t("files.view.grid")}>
                    <AppstoreOutlined />
                  </span>
                ),
                value: "grid"
              },
              {
                label: (
                  <span aria-label={t("files.view.list")}>
                    <BarsOutlined />
                  </span>
                ),
                value: "list"
              }
            ]}
          />
        </Flex>
      )}

      {data.isLoading && !data.hasLoaded ? (
        <Flex vertical gap="middle">
          <Skeleton.Node className="w-full h-32" />
          <Skeleton.Node className="w-full h-32" />
        </Flex>
      ) : isEmpty ? (
        <Card size="small">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical gap={4} align="center">
                <Text strong>{t("globalFiles.company.empty.title", "Company Files is empty")}</Text>
                <Text type="secondary">
                  {t(
                    "globalFiles.company.empty.description",
                    "Upload a company document or create a folder to get started."
                  )}
                </Text>
              </Flex>
            }
          />
        </Card>
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a onClick={() => data.jumpToFolderDepth(0)}>
                    {t("globalFiles.company.title", "Company Files")}
                  </a>
                )
              },
              ...(data.isFolderPathComplete
                ? []
                : [
                    {
                      title: (
                        <Tooltip title={t("files.folder.pathIncomplete")}>
                          <span>…</span>
                        </Tooltip>
                      )
                    }
                  ]),
              ...data.folderPath.map((folder, index) => ({
                title: <a onClick={() => data.jumpToFolderDepth(index + 1)}>{folder.name}</a>
              }))
            ]}
          />
          <Spin spinning={data.isOpeningFolder} delay={FOLDER_LOADER_DELAY_MS}>
            <Flex vertical gap="middle">
              {view === "list" ? (
                <>
                  {createAllowed && <CreateFolderRow onClick={() => actions.setNewFolderOpen(true)} />}
                  {renderFileTable(sortedFileItems, sortedFolders)}
                </>
              ) : (
                <>
                  {renderFolderGrid(sortedFolders)}
                  {!!sortedFileItems.length && renderFileItems(sortedFileItems)}
                </>
              )}
              {data.hasMore && (
                <Flex align="center" justify="center" gap="small">
                  <Text type="secondary">
                    {t("files.showing", { shown: data.loadedTotal, total: data.overallTotal })}
                  </Text>
                  <Button size="small" loading={data.isLoadingMore} onClick={() => void data.loadMoreDocuments()}>
                    {t("files.loadMore")}
                  </Button>
                </Flex>
              )}
            </Flex>
          </Spin>
        </>
      )}

      {actions.previewFile && (
        <FileViewer
          file={actions.previewFile}
          items={previewContext.length ? previewContext : [actions.previewFile]}
          onClose={() => actions.setPreviewFile(null)}
          fileActions={actions.fileActions}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          onRename={(f) => actions.setRenamingFile(f)}
          onMove={(f) => actions.setMovingFiles([f])}
          documentTypeOptions={actions.documentTypeOptions}
          documentTypesFailed={actions.documentTypesFailed}
          onChangeDocumentType={actions.updateDocumentType}
          onUploadNewVersion={(f) => actions.setUploadingNewVersionFile(f)}
          onViewVersionHistory={(f) => actions.setVersionHistoryFile(f)}
          statusOptions={actions.statusOptions}
          statusesFailed={actions.statusesFailed}
          onChangeStatus={actions.updateStatus}
          onRequestReview={(f) => actions.setRequestReviewFile(f)}
          onViewReview={(f) => actions.setReviewingFile(f)}
          onViewComments={(f) => actions.setCommentsFile(f)}
          onViewActivity={(f) => actions.setActivityFile(f)}
          onShare={(f) => actions.setSharingFile(f)}
        />
      )}

      {actions.sharingFile && (
        <ShareDialog file={actions.sharingFile} onClose={() => actions.setSharingFile(null)} />
      )}

      {actions.uploadingNewVersionFile && (
        <UploadNewVersionModal
          file={actions.uploadingNewVersionFile}
          onClose={() => actions.setUploadingNewVersionFile(null)}
          onUploaded={() => {
            actions.setUploadingNewVersionFile(null)
            void data.reload()
          }}
        />
      )}

      {actions.versionHistoryFile && (
        <VersionHistoryDrawer
          open
          fileName={actions.versionHistoryFile.name}
          documentId={actions.versionHistoryFile.id}
          onClose={() => actions.setVersionHistoryFile(null)}
          onPreviewVersion={handlePreviewVersion}
          onRestore={async (version) => !!(await actions.restoreVersion(version.id))}
        />
      )}

      {actions.newFolderOpen && (
        <NewFolderModal
          onClose={() => actions.setNewFolderOpen(false)}
          onSubmit={actions.createFolderHere}
        />
      )}

      {actions.uploadDocumentOpen && (
        <UploadDocumentModal
          folderId={data.currentFolderId}
          onClose={() => actions.setUploadDocumentOpen(false)}
          onUploaded={() => void data.reload()}
        />
      )}

      {actions.renamingFolder && (
        <NewFolderModal
          title={t("files.rename.folderTitle")}
          initialName={actions.renamingFolder.name}
          onClose={() => actions.setRenamingFolder(null)}
          onSubmit={actions.renameFolder}
        />
      )}

      {actions.movingFiles && actions.movingFiles.length > 0 && (
        <MoveFileModal
          files={actions.movingFiles}
          onClose={() => actions.setMovingFiles(null)}
          onSubmit={(folderId) =>
            actions.movingFiles?.length === 1
              ? actions.moveFile(actions.movingFiles[0], folderId)
              : actions.bulkMove(actions.movingFiles ?? [], folderId)
          }
        />
      )}

      {actions.movingFolder && (
        <MoveFolderModal
          folder={actions.movingFolder}
          folders={actions.allFoldersForMove}
          onClose={() => actions.setMovingFolder(null)}
          onSubmit={(parentId) => actions.moveFolderTo(actions.movingFolder?.id ?? "", parentId)}
        />
      )}

      {actions.renamingFile && (
        <RenameFileModal
          fileName={actions.renamingFile.name}
          onClose={() => actions.setRenamingFile(null)}
          onSubmit={(next) => actions.renameFile(actions.renamingFile as FileItem, next)}
        />
      )}

      {actions.assigningDocumentTypeFile && (
        <AssignDocumentTypeModal
          file={actions.assigningDocumentTypeFile}
          options={actions.documentTypeOptions}
          loadFailed={actions.documentTypesFailed}
          onClose={() => actions.setAssigningDocumentTypeFile(null)}
          onSubmit={(value) =>
            actions.updateDocumentType(actions.assigningDocumentTypeFile as FileItem, value)
          }
        />
      )}

      {actions.requestReviewFile && (
        <RequestReviewModal
          file={actions.requestReviewFile}
          onClose={() => actions.setRequestReviewFile(null)}
          onSubmit={actions.submitRequestReview}
        />
      )}

      {actions.reviewingFile && (
        <ReviewDrawer
          file={actions.reviewingFile}
          refreshKey={actions.reviewRefreshKey}
          onClose={() => actions.setReviewingFile(null)}
          onOpenDocument={() => {
            const file = actions.reviewingFile
            if (!file) return
            setPreviewContext([file])
            actions.setPreviewFile(file)
          }}
          onAddComment={() => {
            const file = actions.reviewingFile
            if (file) actions.setCommentsFile(file)
          }}
          onApprove={actions.approveReview}
          onReject={(review) => actions.setRejectingReview(review)}
          onRequestChanges={(review) => actions.setRequestingChangesReview(review)}
        />
      )}

      {actions.rejectingReview && actions.reviewingFile && (
        <RejectReviewModal
          fileName={actions.reviewingFile.name}
          onClose={() => actions.setRejectingReview(null)}
          onSubmit={actions.rejectReview}
        />
      )}

      {actions.requestingChangesReview && actions.reviewingFile && (
        <RejectReviewModal
          fileName={actions.reviewingFile.name}
          onClose={() => actions.setRequestingChangesReview(null)}
          onSubmit={actions.requestChanges}
          danger={false}
          title={t("files.review.requestChangesTitle")}
          submitText={t("files.review.requestChangesSubmit")}
          reasonLabel={t("files.review.requestChangesReason")}
          placeholder={t("files.review.requestChangesPlaceholder")}
        />
      )}

      {actions.commentsFile && (
        <CommentsDrawer file={actions.commentsFile} onClose={() => actions.setCommentsFile(null)} />
      )}

      {actions.activityFile && (
        <ActivityDrawer file={actions.activityFile} onClose={() => actions.setActivityFile(null)} />
      )}
    </Flex>
  )
}
